import { memo, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18next from "config/lang/i18n";

import section1DDHLogo from "assets/images/logos/TheADDH_black.png";
import section1FALogo from "assets/images/logos/TheAF&A_black.png";
import section1TELELogo from "assets/images/logos/TheATELE&EOS_black.png";
import section1RLogo from "assets/images/logos/TheRMSK_black.png";

import section1RImg from "assets/images/solution/solution_section1_r.png";

import ddhVideo from "assets/videos/solution/solution_ddh.mov";
import faVideo from "assets/videos/solution/solution_fa.mov";
import teleVideo from "assets/videos/solution/solution_tele.mov";

import SolutionItem from "./SolutionItem";
import { useLocation } from "react-router-dom";

function SolutionItems() {
  const { t } = useTranslation();
  const videoRefs = useRef([]);
  const { search } = useLocation();

  useEffect(() => {
    // 각 동영상마다 다른 시점을 썸네일로 설정
    const thumbnailTimes = [34, 15, 39]; // DDH: 34초, FA: 15초, TELE: 39초

    videoRefs.current.forEach((video, index) => {
      if (video) {
        let hasPlayed = false; // 처음 재생 여부를 추적

        video.addEventListener("loadedmetadata", () => {
          video.currentTime = thumbnailTimes[index] || 0; // 각 동영상마다 다른 시점 설정
        });

        // 처음 재생할 때만 0초로 돌아가기
        video.addEventListener("play", () => {
          if (!hasPlayed) {
            video.currentTime = 0;
            hasPlayed = true;
          }
        });

        // 비디오가 끝나면 다시 처음부터 재생 가능하도록 리셋
        video.addEventListener("ended", () => {
          hasPlayed = false;
        });
      }
    });
  }, []);

  // URL 파라미터로 특정 섹션으로 스크롤
  useEffect(() => {
    if (!search) return;
    const params = new URLSearchParams(search);
    const sw = params.get("sw");

    if (sw) {
      // 스크롤할 섹션의 인덱스 찾기
      const sectionMap = {
        ddh: 0,
        fa: 1,
        tele: 2,
        r: 3,
      };

      const targetIndex = sectionMap[sw];
      if (targetIndex !== undefined) {
        // 약간의 지연을 두고 스크롤 (컴포넌트가 렌더링된 후)
        setTimeout(() => {
          const targetElement = document.querySelector(
            `[data-section-index="${targetIndex}"]`,
          );
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      }
    }
  }, [search]);

  const section1Items = useMemo(
    () => [
      {
        item: {
          name: "ddh_project",
          itemlogo: <img src={section1DDHLogo} alt="DDH logo" />,
          subtitle: t(`solution.section1Item.item1.subtitle`),
          explanation: {
            text: [
              t(`solution.section1Item.item1.explanation.text1`),
              t(`solution.section1Item.item1.explanation.text2`),
            ],
            subText: [
              t(`solution.section1Item.item1.subText.text1`),
              t(`solution.section1Item.item1.subText.text2`),
              t(`solution.section1Item.item1.subText.text3`),
              t(`solution.section1Item.item1.subText.text4`),
            ],
          },
          itemImage: (
            <video
              ref={(el) => (videoRefs.current[0] = el)}
              src={ddhVideo}
              alt="DDH Video"
              controls
              muted
              loop
              preload="metadata"
            />
          ),

          url: "https://www.youtube.com/watch?v=9NtxY5UHqFU",
        },
      },
      {
        item: {
          name: "fa_project",
          itemlogo: <img src={section1FALogo} alt="FA logo" />,
          subtitle: t(`solution.section1Item.item2.subtitle`),
          explanation: {
            text: [
              t(`solution.section1Item.item2.explanation.text1`),
              t(`solution.section1Item.item2.explanation.text2`),
            ],
          },
          itemImage: (
            <video
              ref={(el) => (videoRefs.current[1] = el)}
              src={faVideo}
              alt="FA Video"
              controls
              muted
              loop
              preload="metadata"
            />
          ),
          url: "https://www.youtube.com/watch?v=PEzS7KjiCNo",
        },
      },
      {
        item: {
          name: "tele_project",
          itemlogo: <img src={section1TELELogo} alt="TELE logo" />,
          subtitle: t(`solution.section1Item.item3.subtitle`),
          explanation: {
            text: [
              t(`solution.section1Item.item3.explanation.text1`),
              t(`solution.section1Item.item3.explanation.text2`),
            ],
          },
          itemImage: (
            <video
              ref={(el) => (videoRefs.current[2] = el)}
              src={teleVideo}
              alt="TELE Video"
              controls
              muted
              loop
              preload="metadata"
            />
          ),
          url: "https://youtu.be/VHfbpUolrh8",
        },
      },
      {
        item: {
          name: "r_project",
          itemlogo: <img src={section1RLogo} alt="R logo" />,
          subtitle: t(`solution.section1Item.item4.subtitle`),
          explanation: {
            text: [
              t(`solution.section1Item.item4.explanation.text1`),
              t(`solution.section1Item.item4.explanation.text2`),
            ],
          },
          itemImage: <img src={section1RImg} alt="R Img" />,
          url: "https://www.youtube.com/watch?v=oM-uG8ArHR0",
        },
      },
    ],
    [t, i18next.language],
  );

  return (
    <div className="solutionSection1Items">
      {section1Items.map((item, idx) => (
        <SolutionItem key={idx} item={item} sectionIndex={idx} />
      ))}
    </div>
  );
}

export default SolutionItems;
